import { useState, useMemo, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { supabase } from '../supabase'
import { useBrand } from '../hooks/useBrand'
import PhoneInput from '../components/PhoneInput'
import CurrencyPicker from '../components/CurrencyPicker'
import { CURRENCY_LIST } from '../utils/currencies'

const NAVY  = '#0B1F3A'
const TEAL  = '#2DD4B0'
const MUTED = '#6B7280'
const BORDER= '#E5E7EB'
const BG    = '#F8FAFC'

const inp = {
  width: '100%', border: '1.5px solid ' + BORDER, borderRadius: '10px',
  padding: '10px 12px', fontSize: '13px', outline: 'none',
  fontFamily: 'Space Grotesk, sans-serif', boxSizing: 'border-box',
  color: '#111827', background: '#fff', transition: 'border-color 0.15s',
}
const iF = e => { e.target.style.borderColor = TEAL }
const iB = e => { e.target.style.borderColor = BORDER }

function generatePromoCode(shopName) {
  const base   = (shopName || 'SHOP').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6) || 'SHOP'
  const suffix = Math.random().toString(36).toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 4)
  return base + '-' + suffix
}

const FEATURES = [
  { path: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2', text: 'Order management' },
  { path: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z', text: 'PDF receipts' },
  { path: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z', text: 'Sales analytics' },
  { path: 'M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9', text: 'Free shop page' },
  { path: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z', text: 'CRM & customers' },
  { path: 'M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7', text: 'Earn up to ৳500' },
]

function HeroPanel({ heroPhoto, brand }) {
  return (
    <div style={{ background: 'linear-gradient(160deg,' + NAVY + ' 0%,#0D2B4E 60%,#0A2240 100%)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', padding: '48px 40px', position: 'relative', overflow: 'hidden', minHeight: '100vh' }}>
      <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '300px', height: '300px', borderRadius: '50%', background: 'rgba(45,212,176,0.06)' }} />
      <div style={{ position: 'absolute', bottom: '-80px', left: '-40px', width: '350px', height: '350px', borderRadius: '50%', background: 'rgba(45,212,176,0.04)' }} />

      <div style={{ position: 'relative' }}>
        {brand.logo_light_url
          ? <img src={brand.logo_light_url} alt={brand.name} style={{ height: '32px', objectFit: 'contain', marginBottom: '8px', display: 'block', filter: 'brightness(0) invert(1)' }} />
          : <div style={{ fontSize: '24px', fontWeight: 800, color: '#fff', letterSpacing: '-0.5px', marginBottom: '6px' }}>{brand.name || 'PageConnect'}</div>
        }
        <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)' }}>Sell smarter. Run like a business.</div>
      </div>

      <div style={{ position: 'relative', flex: 1, margin: '32px 0', borderRadius: '20px', overflow: 'hidden', minHeight: '320px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}>
        {heroPhoto ? (
          <>
            <img src={heroPhoto} alt="Seller" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top', position: 'absolute', inset: 0 }} />
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top,rgba(11,31,58,0.8) 0%,transparent 55%)' }} />
          </>
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '320px' }}>
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="rgba(45,212,176,0.3)" strokeWidth="1" strokeLinecap="round"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4zM3 6h18M16 10a4 4 0 01-8 0"/></svg>
          </div>
        )}
        {/* Stats badges */}
        <div style={{ position: 'absolute', top: '16px', right: '16px', background: 'rgba(255,255,255,0.96)', borderRadius: '14px', padding: '10px 14px', boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}>
          <div style={{ fontSize: '10px', color: MUTED }}>Today's revenue</div>
          <div style={{ fontSize: '18px', fontWeight: 800, color: NAVY }}>৳12,500</div>
          <div style={{ fontSize: '11px', color: '#16A34A', fontWeight: 600 }}>↑ 24% vs yesterday</div>
        </div>
        <div style={{ position: 'absolute', bottom: '16px', left: '16px', background: 'rgba(255,255,255,0.96)', borderRadius: '14px', padding: '10px 14px', boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}>
          <div style={{ fontSize: '10px', color: MUTED }}>New orders today</div>
          <div style={{ fontSize: '18px', fontWeight: 800, color: NAVY }}>18</div>
          <div style={{ fontSize: '11px', color: TEAL, fontWeight: 700 }}>3 pending</div>
        </div>
        {/* Teal dot */}
        <div style={{ position: 'absolute', top: '16px', left: '16px', width: '8px', height: '8px', borderRadius: '50%', background: TEAL, boxShadow: '0 0 10px ' + TEAL }} />
      </div>

      <div style={{ position: 'relative', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
        {FEATURES.map((f, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '24px', height: '24px', borderRadius: '6px', background: 'rgba(45,212,176,0.1)', border: '1px solid rgba(45,212,176,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2" strokeLinecap="round"><path d={f.path}/></svg>
            </div>
            <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.75)' }}>{f.text}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function Signup() {
  const { signUp } = useAuth()
  const { brand }  = useBrand()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const heroPhoto = useMemo(() => {
    const slots = ['signup_hero_1','signup_hero_2','signup_hero_3','signup_hero_4']
    const available = slots.map(k => brand[k]).filter(Boolean)
    if (!available.length) return null
    return available[Math.floor(Math.random() * available.length)]
  }, []) // eslint-disable-line

  const [step, setStep]             = useState(0)
  const [sellerMode, setSellerMode] = useState('')
  const [form, setForm] = useState({
    shopName: '', facebookUrl: '',
    phoneCountryCode: '+880', phone: '',
    email: '', password: '', confirmPassword: '',
    country: '', seller_currency: 'BDT', referralCode: '',
  })
  const [agreed, setAgreed]                     = useState(false)
  const [error, setError]                       = useState('')
  const [loading, setLoading]                   = useState(false)
  const [referralValid, setReferralValid]       = useState(null)
  const [referralChecking, setReferralChecking] = useState(false)
  const [referralFromLink, setReferralFromLink] = useState(false)
  const [otpCode, setOtpCode]                   = useState('')
  const [otpLoading, setOtpLoading]             = useState(false)
  const [otpError, setOtpError]                 = useState('')
  const [resending, setResending]               = useState(false)
  const [resendCooldown, setResendCooldown]     = useState(false)
  const [showPass, setShowPass]                 = useState(false)
  const [showConfirm, setShowConfirm]           = useState(false)

  useEffect(() => {
    const refFromUrl = searchParams.get('ref')
    if (refFromUrl) {
      const code = refFromUrl.toUpperCase()
      setForm(f => ({ ...f, referralCode: code }))
      setReferralFromLink(true)
      checkReferralCode(code)
    }
  }, []) // eslint-disable-line

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }))
  const facebookUsername = form.facebookUrl?.split('/').filter(Boolean).pop() || ''
  const previewUrl       = facebookUsername ? `pageconnect.io/${facebookUsername}` : ''
  const isInt            = sellerMode === 'international'
  const selectedCurrency = CURRENCY_LIST.find(c => c.code === form.seller_currency)

  const checkReferralCode = async (code) => {
    if (!code || code.length < 4) { setReferralValid(null); return }
    setReferralChecking(true)
    const { data } = await supabase.from('sellers').select('id').eq('promo_code', code.toUpperCase()).single()
    setReferralValid(!!data); setReferralChecking(false)
  }

  const startCooldown = () => {
    setResendCooldown(60)
    const iv = setInterval(() => setResendCooldown(p => { if (p <= 1) { clearInterval(iv); return 0 } return p - 1 }), 1000)
  }

  const handleSignup = async () => {
    if (!form.shopName || !form.email || !form.password || !form.phone) { setError('Please fill in all required fields'); return }
    if (!form.facebookUrl) { setError('Facebook page URL is required'); return }
    if (isInt && !form.country) { setError('Please select your country'); return }
    if (form.password !== form.confirmPassword) { setError('Passwords do not match'); return }
    if (form.password.length < 6) { setError('Password must be at least 6 characters'); return }
    if (!agreed) { setError('Please agree to the terms and conditions'); return }
    if (form.referralCode && referralValid === false) { setError('Invalid referral code'); return }
    setLoading(true); setError('')

    const fullPhone = form.phoneCountryCode + form.phone
    const promoCode = generatePromoCode(form.shopName)

    const { error: signUpError, data: signUpData } = await signUp({
      email: form.email, password: form.password,
      shopName: form.shopName, phone: fullPhone,
      phone_country_code: form.phoneCountryCode,
      facebookUrl: form.facebookUrl, sellerMode,
      currency: form.seller_currency, seller_currency: form.seller_currency,
      country: form.country || null, promo_code: promoCode,
    })
    if (signUpError) { setError(signUpError.message); setLoading(false); return }

    if (signUpData?.user || signUpData?.session) {
      const { data: sellerData } = await supabase.from('sellers').select('id').eq('email', form.email).single()
      if (sellerData?.id) {
        await supabase.from('sellers').update({ promo_code: promoCode }).eq('id', sellerData.id)
        if (form.referralCode && referralValid) {
          const { data: ref } = await supabase.from('sellers').select('id').eq('promo_code', form.referralCode.toUpperCase()).single()
          if (ref?.id) await supabase.from('referrals').insert({ referrer_seller_id: ref.id, referee_seller_id: sellerData.id, promo_code: form.referralCode.toUpperCase(), status: 'pending', currency: form.seller_currency === 'BDT' ? 'BDT' : 'USD' })
        }
      }
    }
    setLoading(false)
    if (signUpData?.session) { navigate('/dashboard') } else { setStep(2); startCooldown() }
  }

  const handleVerifyOtp = async () => {
    if (otpCode.length < 6) { setOtpError('Please enter the 6-digit code'); return }
    setOtpLoading(true); setOtpError('')
    const { error } = await supabase.auth.verifyOtp({ email: form.email, token: otpCode.trim(), type: 'signup' })
    if (error) { setOtpError(error.message || 'Invalid code — check your email and try again'); setOtpLoading(false); return }
    navigate('/dashboard')
  }

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return
    setResending(true)
    await supabase.auth.resend({ type: 'signup', email: form.email })
    setResending(false); startCooldown()
  }

  const ErrBox = ({ msg }) => !msg ? null : (
    <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '10px', padding: '10px 12px', marginBottom: '14px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0, marginTop: '1px' }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
      <span style={{ fontSize: '12.5px', color: '#B91C1C', lineHeight: 1.5 }}>{msg}</span>
    </div>
  )

  const Lbl = ({ children }) => (
    <label style={{ fontSize: '11.5px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '5px' }}>{children}</label>
  )

  const PwField = ({ value, onChange, show, onToggle, placeholder }) => (
    <div style={{ position: 'relative' }}>
      <input type={show ? 'text' : 'password'} value={value} onChange={onChange}
        placeholder={placeholder || '••••••••'} style={{ ...inp, paddingRight: '52px' }}
        onFocus={iF} onBlur={iB} />
      <button type="button" onClick={onToggle}
        style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: MUTED, fontSize: '11.5px', fontWeight: 600, padding: '2px', fontFamily: 'inherit' }}>
        {show ? 'Hide' : 'Show'}
      </button>
    </div>
  )

  // ── OTP step ──
  if (step === 2) {
    return (
      <div style={{ minHeight: '100vh', background: BG, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', fontFamily: 'Space Grotesk, sans-serif' }}>
        <style>{`@keyframes sg-spin { to { transform: rotate(360deg) } }`}</style>
        <div style={{ background: '#fff', borderRadius: '20px', padding: '40px 32px', width: '100%', maxWidth: '420px', boxShadow: '0 8px 40px rgba(11,31,58,0.12)', border: '1px solid ' + BORDER, textAlign: 'center' }}>
          <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'rgba(45,212,176,0.1)', border: '2px solid rgba(45,212,176,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="1.8" strokeLinecap="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
          </div>
          <div style={{ fontSize: '20px', fontWeight: 800, color: '#111827', marginBottom: '8px' }}>Check your email</div>
          <div style={{ fontSize: '13px', color: MUTED, marginBottom: '4px' }}>We sent a 6-digit code to</div>
          <div style={{ fontSize: '14px', fontWeight: 700, color: NAVY, marginBottom: '24px' }}>{form.email}</div>
          <ErrBox msg={otpError} />
          <div style={{ marginBottom: '16px', textAlign: 'left' }}>
            <Lbl>6-digit confirmation code</Lbl>
            <input type="text" value={otpCode} onChange={e => setOtpCode(e.target.value.replace(/\D/g,'').slice(0,6))}
              onKeyDown={e => e.key === 'Enter' && handleVerifyOtp()}
              placeholder="000000" maxLength={6} autoFocus
              style={{ ...inp, textAlign: 'center', fontSize: '28px', fontWeight: 700, letterSpacing: '0.3em', fontFamily: 'JetBrains Mono, monospace', padding: '16px' }}
              onFocus={iF} onBlur={iB} />
          </div>
          {/* Pin dots */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '20px' }}>
            {[0,1,2,3,4,5].map(i => (
              <div key={i} style={{ width: '10px', height: '10px', borderRadius: '50%', background: otpCode.length > i ? TEAL : BORDER, transition: 'background 0.15s' }} />
            ))}
          </div>
          <button onClick={handleVerifyOtp} disabled={otpLoading || otpCode.length < 6}
            style={{ width: '100%', padding: '13px', borderRadius: '12px', background: otpCode.length === 6 && !otpLoading ? NAVY : BORDER, border: 'none', fontSize: '14px', fontWeight: 700, color: otpCode.length === 6 ? '#fff' : '#9CA3AF', cursor: otpCode.length === 6 && !otpLoading ? 'pointer' : 'not-allowed', marginBottom: '16px', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'background 0.15s' }}>
            {otpLoading && <div style={{ width: '16px', height: '16px', borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', animation: 'sg-spin 0.7s linear infinite' }} />}
            {otpLoading ? 'Verifying...' : 'Confirm email →'}
          </button>
          <div style={{ fontSize: '12px', color: MUTED, marginBottom: '6px' }}>Didn't receive the code?</div>
          <button onClick={handleResendOtp} disabled={resendCooldown > 0 || resending}
            style={{ fontSize: '13px', fontWeight: 600, color: resendCooldown > 0 ? MUTED : NAVY, background: 'none', border: 'none', cursor: resendCooldown > 0 ? 'not-allowed' : 'pointer', fontFamily: 'inherit', marginBottom: '20px' }}>
            {resending ? 'Sending...' : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
          </button>
          <div style={{ fontSize: '11.5px', color: MUTED, padding: '10px 12px', background: BG, borderRadius: '10px', textAlign: 'left' }}>
            Check your spam folder if you don't see it. Code expires in 1 hour.
          </div>
          <button onClick={() => setStep(1)} style={{ fontSize: '12px', color: MUTED, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', marginTop: '12px' }}>← Back to signup</button>
        </div>
      </div>
    )
  }

  const heroPanel = (
    <div style={{ flex: '0 0 60%', display: 'none' }} className="sg-hero">
      <HeroPanel heroPhoto={heroPhoto} brand={brand} />
    </div>
  )

  const sharedStyle = `
    @keyframes sg-spin { to { transform: rotate(360deg) } }
    .sg-hero { display: flex !important; }
    @media(max-width: 768px) { .sg-hero { display: none !important; } }
  `

  // ── STEP 0 — Location ──
  if (step === 0) {
    return (
      <div style={{ minHeight: '100vh', background: BG, display: 'flex', fontFamily: 'Space Grotesk, sans-serif' }}>
        {heroPanel}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 32px', overflowY: 'auto', minHeight: '100vh' }}>
          <div style={{ width: '100%', maxWidth: '400px' }}>
            <div style={{ fontSize: '22px', fontWeight: 800, color: '#111827', marginBottom: '4px' }}>Where is your business?</div>
            <div style={{ fontSize: '13px', color: MUTED, marginBottom: '28px' }}>We set up your account for your location and currency</div>

            {referralFromLink && referralValid && (
              <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '12px', padding: '12px 16px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2" strokeLinecap="round"><path d="M20 12v10H4V12M22 7H2v5h20V7zM12 22V7M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z"/></svg>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#16A34A' }}>Referral discount applied!</div>
                  <div style={{ fontSize: '12px', color: '#059669', marginTop: '2px' }}>Code <strong>{form.referralCode}</strong> — you both get a discount when you subscribe.</div>
                </div>
              </div>
            )}

            {[
              { mode: 'bangladesh', flag: '🇧🇩', title: 'Based in Bangladesh', sub: 'BDT ৳ · Bangladesh address · Local couriers', currency: 'BDT', code: '+880' },
              { mode: 'international', flag: '🌍', title: 'Based outside Bangladesh', sub: 'Choose your currency · International address', currency: 'USD', code: '+61' },
            ].map(opt => (
              <button key={opt.mode}
                onClick={() => { setSellerMode(opt.mode); set('seller_currency', opt.currency); set('phoneCountryCode', opt.code); setStep(1) }}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '14px', padding: '16px', borderRadius: '14px', border: '2px solid ' + BORDER, background: '#fff', marginBottom: '10px', cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s', fontFamily: 'inherit' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = TEAL; e.currentTarget.style.background = 'rgba(45,212,176,0.04)' }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = BORDER; e.currentTarget.style.background = '#fff' }}>
                <span style={{ fontSize: '32px' }}>{opt.flag}</span>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: '#111827' }}>{opt.title}</div>
                  <div style={{ fontSize: '12px', color: MUTED, marginTop: '2px' }}>{opt.sub}</div>
                </div>
              </button>
            ))}

            {!referralFromLink && (
              <div style={{ background: 'rgba(45,212,176,0.06)', borderRadius: '12px', padding: '14px 16px', marginBottom: '24px', border: '1px solid rgba(45,212,176,0.2)', marginTop: '8px' }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: NAVY, marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2" strokeLinecap="round"><path d="M20 12v10H4V12M22 7H2v5h20V7zM12 22V7M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z"/></svg>
                  Refer & Earn
                </div>
                <div style={{ fontSize: '12px', color: MUTED, lineHeight: 1.6 }}>Have a referral code? Enter it on the next step — you both get a discount.</div>
              </div>
            )}

            <div style={{ textAlign: 'center', fontSize: '13px', color: MUTED }}>
              Already have an account? <Link to="/login" style={{ fontWeight: 600, color: NAVY, textDecoration: 'none' }}>Sign in</Link>
            </div>
          </div>
        </div>
        <style>{sharedStyle}</style>
      </div>
    )
  }

  // ── STEP 1 — Signup form ──
  return (
    <div style={{ minHeight: '100vh', background: BG, display: 'flex', fontFamily: 'Space Grotesk, sans-serif' }}>
      {heroPanel}
      <div style={{ flex: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 32px', overflowY: 'auto', minHeight: '100vh' }}>
        <div style={{ width: '100%', maxWidth: '420px', paddingBottom: '40px' }}>
          <button onClick={() => setStep(0)} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: MUTED, background: 'none', border: 'none', cursor: 'pointer', marginBottom: '20px', padding: 0, fontFamily: 'inherit' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
            Change location
          </button>

          <div style={{ fontSize: '20px', fontWeight: 800, color: '#111827', marginBottom: '2px' }}>Create your account</div>
          <div style={{ fontSize: '12px', color: MUTED, marginBottom: '24px' }}>
            {isInt ? '🌍 International seller' : '🇧🇩 Bangladesh seller'} · 14-day free trial · No credit card
          </div>

          <ErrBox msg={error} />

          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

            <div>
              <Lbl>Shop name / Facebook page name *</Lbl>
              <input type="text" value={form.shopName} onChange={e => set('shopName', e.target.value)}
                placeholder={isInt ? "e.g. Riya's Boutique Sydney" : "e.g. Riya's Boutique"}
                style={inp} onFocus={iF} onBlur={iB} />
            </div>

            {isInt && <>
              <CurrencyPicker label="Your currency *" value={form.seller_currency} onChange={v => set('seller_currency', v)} />
              <div>
                <Lbl>Country *</Lbl>
                <input type="text" value={form.country} onChange={e => set('country', e.target.value)}
                  placeholder="e.g. Australia, United Kingdom, UAE"
                  style={inp} onFocus={iF} onBlur={iB} />
              </div>
            </>}

            <div>
              <Lbl>Facebook page URL *</Lbl>
              <input type="text" value={form.facebookUrl} onChange={e => set('facebookUrl', e.target.value)}
                placeholder="https://facebook.com/yourpage"
                style={inp} onFocus={iF} onBlur={iB} />
              {previewUrl && (
                <div style={{ marginTop: '6px', background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '8px', padding: '7px 10px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#16A34A' }}>Your PageConnect page:</div>
                  <div style={{ fontSize: '11px', color: '#059669', fontFamily: 'JetBrains Mono, monospace', marginTop: '2px' }}>{previewUrl}</div>
                </div>
              )}
            </div>

            <PhoneInput label="Phone number *" countryCode={form.phoneCountryCode} phone={form.phone}
              onCountryCodeChange={v => set('phoneCountryCode', v)} onPhoneChange={v => set('phone', v)}
              placeholder={isInt ? '412345678' : '1712345678'} required />

            <div>
              <Lbl>Email address * <span style={{ fontWeight: 400 }}>used to sign in</span></Lbl>
              <input type="email" value={form.email} onChange={e => set('email', e.target.value)}
                placeholder="your@email.com" style={inp} onFocus={iF} onBlur={iB} />
            </div>

            <div>
              <Lbl>Password * <span style={{ fontWeight: 400 }}>min 6 characters</span></Lbl>
              <PwField value={form.password} onChange={e => set('password', e.target.value)} show={showPass} onToggle={() => setShowPass(s => !s)} />
            </div>

            <div>
              <Lbl>Confirm password *</Lbl>
              <PwField value={form.confirmPassword} onChange={e => set('confirmPassword', e.target.value)} show={showConfirm} onToggle={() => setShowConfirm(s => !s)} />
            </div>

            {/* Referral code */}
            <div style={{ background: referralFromLink && referralValid ? '#F0FDF4' : 'rgba(45,212,176,0.05)', borderRadius: '12px', padding: '14px', border: '1px solid', borderColor: referralFromLink && referralValid ? '#BBF7D0' : 'rgba(45,212,176,0.2)' }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: referralFromLink && referralValid ? '#16A34A' : NAVY, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M20 12v10H4V12M22 7H2v5h20V7zM12 22V7M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z"/></svg>
                {referralFromLink && referralValid ? 'Referral discount applied!' : 'Have a referral code? (optional)'}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input type="text" value={form.referralCode}
                  onChange={e => { set('referralCode', e.target.value.toUpperCase()); setReferralValid(null); setReferralFromLink(false) }}
                  onBlur={() => checkReferralCode(form.referralCode)}
                  placeholder="e.g. RIYAS-X4K2"
                  style={{ flex: 1, border: '1.5px solid', borderColor: referralValid===true?'#16A34A':referralValid===false?'#DC2626':BORDER, borderRadius: '9px', padding: '9px 12px', fontSize: '13px', outline: 'none', fontFamily: 'JetBrains Mono, monospace', color: '#111827', background: '#fff', boxSizing: 'border-box' }} />
                {referralChecking && <div style={{ display: 'flex', alignItems: 'center', fontSize: '12px', color: MUTED }}>Checking...</div>}
                {referralValid === true  && <div style={{ display: 'flex', alignItems: 'center' }}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg></div>}
                {referralValid === false && <div style={{ display: 'flex', alignItems: 'center' }}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></div>}
              </div>
              {referralValid === true  && <div style={{ fontSize: '11.5px', color: '#16A34A', marginTop: '6px', fontWeight: 600 }}>Valid code! You both get a discount when you subscribe.</div>}
              {referralValid === false && <div style={{ fontSize: '11.5px', color: '#DC2626', marginTop: '6px' }}>Code not found — check for typos or leave blank.</div>}
              <div style={{ fontSize: '11px', color: MUTED, marginTop: '6px', lineHeight: 1.5 }}>
                {isInt ? 'Both get $10 off monthly or $25 off yearly.' : 'Both get ৳50 off monthly or ৳500 off yearly.'}
              </div>
            </div>

            <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '10px', padding: '10px 12px', fontSize: '12px', color: '#1E40AF' }}>
              PageConnect will never ask for your OTP or password. If anyone does — it is a scam.
            </div>

            {isInt && selectedCurrency && (
              <div style={{ background: BG, border: '1px solid ' + BORDER, borderRadius: '10px', padding: '12px', fontSize: '12px', color: MUTED, lineHeight: 1.6 }}>
                <strong style={{ color: '#111827' }}>International notes:</strong>
                <div style={{ marginTop: '6px' }}>· Management platform only — not a payment gateway.<br />· You are responsible for local tax and trade laws.<br />· Currency ({selectedCurrency.code} {selectedCurrency.symbol}) locked after registration.</div>
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)}
                style={{ width: '16px', height: '16px', marginTop: '2px', flexShrink: 0, accentColor: NAVY, cursor: 'pointer' }} />
              <label style={{ fontSize: '12px', color: MUTED, lineHeight: 1.6, cursor: 'pointer' }} onClick={() => setAgreed(!agreed)}>
                I agree to the <a href="https://pageconnect.io/terms.html" target="_blank" rel="noreferrer" style={{ color: NAVY }}>Terms</a> and <a href="https://pageconnect.io/privacy.html" target="_blank" rel="noreferrer" style={{ color: NAVY }}>Privacy Policy</a>.
                {' '}All subscription payments are <strong>non-refundable</strong>.
                {isInt && ' I am responsible for my local tax and legal obligations.'}
              </label>
            </div>

            <button onClick={handleSignup} disabled={loading}
              style={{ width: '100%', padding: '14px', borderRadius: '12px', background: loading ? BORDER : NAVY, border: 'none', fontSize: '14px', fontWeight: 700, color: loading ? '#9CA3AF' : '#fff', cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              {loading && <div style={{ width: '16px', height: '16px', borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', animation: 'sg-spin 0.7s linear infinite' }} />}
              {loading ? 'Creating account...' : 'Create account — free 14 days'}
            </button>

            <div style={{ textAlign: 'center', fontSize: '13px', color: MUTED }}>
              Already have an account? <Link to="/login" style={{ fontWeight: 600, color: NAVY, textDecoration: 'none' }}>Sign in</Link>
            </div>
          </div>
        </div>
      </div>
      <style>{sharedStyle}</style>
    </div>
  )
}

export default Signup